import { formatMonthYear } from '@tcl-argon-utils/utils';
import cn from 'classnames';
import { CaretUp } from '@tcl-boron-icons/icons';
import { Classes } from 'jss';
import { ActionPalette, GrayscalePalette } from '@tcl-boron-colors/colors';
import * as React from 'react';
import { createUseStyles } from 'react-jss';
import { useScrollbarStyles } from '@tcl-boron-styles/hooks';

const calendarVariantStyles = {
  ['one calendar']: {
    height: '272px',
    width: '272px',
  },
  ['two calendars']: {
    height: '253px',
    width: '545px',
  },
};

interface YearPickerStylesData {
  calendarVariant: CalendarVariant;
}

const useStyles: (data: YearPickerStylesData) => Classes = createUseStyles({
  root: {
    height: ({ calendarVariant }: YearPickerStylesData) => calendarVariantStyles[calendarVariant].height,
    width: ({ calendarVariant }: YearPickerStylesData) => calendarVariantStyles[calendarVariant].width,
    overflowY: 'scroll',
  },
  header: {
    marginBottom: '12px',
    paddingLeft: '24px',
    paddingTop: '16px',
    fontFamily: 'inherit',
  },
  yearOptionsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '14px 10px',
    paddingLeft: '16px',
    paddingBottom: '16px',
  },
  yearOption: {
    height: '24px',
    width: '48px',
    borderRadius: '24px',
    color: ActionPalette[600],
    border: 'none',
    backgroundColor: 'transparent',
    fontWeight: 700,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: GrayscalePalette[300],
    },
  },
  yearToggle: {
    border: 'none',
    backgroundColor: 'transparent',
    cursor: 'pointer',
    fontSize: '14px',
    lineHeight: '20px',
    fontWeight: 700,
    padding: '0px',
    fontFamily: 'inherit',
    color: ActionPalette[600],
  },
  isSelected: {
    backgroundColor: ActionPalette[600],
    color: GrayscalePalette[100],
  },
});

export type CalendarVariant = 'one calendar' | 'two calendars';

interface YearPickerProps {
  month: number;
  year: number;
  minYear: number;
  maxYear: number;
  onClickYear: (year: number) => void;
  calendarVariant: CalendarVariant;
}

export const YearPicker: React.FC<YearPickerProps> = ({
  month,
  year,
  minYear,
  maxYear,
  calendarVariant,
  onClickYear: handleClickYear,
}) => {
  const classes = useStyles({ calendarVariant });
  const scrollClasses = useScrollbarStyles();

  const yearOptions: number[] = new Array(maxYear + 1 - minYear).fill(null).map((_, i) => minYear + i);

  return (
    <div className={cn(classes.root, scrollClasses.scrollbar)}>
      <div className={classes.header}>
        <button className={classes.yearToggle} onClick={() => handleClickYear(year)}>
          {formatMonthYear(month, year)} <CaretUp height="10px" width="10px" />
        </button>
      </div>
      <div className={classes.yearOptionsContainer}>
        {yearOptions.map((yo) => (
          <button
            onClick={() => handleClickYear(yo)}
            className={cn(classes.yearOption, { [classes.isSelected]: year === yo })}
            key={yo}
          >
            {yo}
          </button>
        ))}
      </div>
    </div>
  );
};
